import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import type {
  TransactionCategory,
  TransactionImportRowLineDTO,
  TransactionType,
} from '@inversor-hub/types';
import { EurCurrencyField } from '@/components/form/EurCurrencyField';
import { formatCurrency } from '@/utils/misc';
import { TransactionCategorySelect } from '../../TransactionCategorySelect';
import { expenseCategories, incomeCategories } from '../../constants';

interface DraftLine {
  category: TransactionCategory | '';
  amount: number | null;
}

interface PreviewRowSplitEditorProps {
  total: number;
  type: TransactionType;
  suggestedCategories?: TransactionCategory[];
  value?: TransactionImportRowLineDTO[];
  onApply: (lines: TransactionImportRowLineDTO[]) => void;
  onClear: () => void;
}

type SplitPreset = [TransactionCategory, TransactionCategory];

const EXPENSE_PRESETS: { label: string; tooltip: string; categories: SplitPreset }[] = [
  {
    label: 'Hipoteca',
    tooltip: 'Reparte la cuota entre intereses (deducibles) y devolución de capital.',
    categories: ['FINANCING_INTEREST', 'LOAN_PRINCIPAL'],
  },
  {
    label: 'Reforma',
    tooltip: 'Separa conservación y reparación de la mejora que aumenta valor.',
    categories: ['REPAIRS_MAINTENANCE', 'IMPROVEMENT'],
  },
];

function buildInitialLines(
  value: TransactionImportRowLineDTO[] | undefined,
  suggested: TransactionCategory[] | undefined,
  type: TransactionType,
  total: number
): [DraftLine, DraftLine] {
  const allowed = type === 'EXPENSE' ? expenseCategories : incomeCategories;
  const isAllowed = (category: TransactionCategory) => allowed.includes(category);

  if (value && value.length > 0) {
    return [
      { category: value[0].category, amount: value[0].amount },
      value[1]
        ? { category: value[1].category, amount: value[1].amount }
        : { category: '', amount: total - value[0].amount },
    ];
  }

  const seed = (suggested ?? []).filter(isAllowed).slice(0, 2);
  return [
    { category: seed[0] ?? '', amount: null },
    { category: seed[1] ?? '', amount: null },
  ];
}

export const PreviewRowSplitEditor = ({
  total,
  type,
  suggestedCategories,
  value,
  onApply,
  onClear,
}: PreviewRowSplitEditorProps) => {
  const [lines, setLines] = useState<[DraftLine, DraftLine]>(() =>
    buildInitialLines(value, suggestedCategories, type, total)
  );

  // Re-seed when the applied split, recommendation, or the row's type/total
  // change underneath us (e.g. refreshing the preview returns canonical lines or
  // an updated recommendation). These props are stable per row, so this does not
  // clobber in-progress edits during a single editing session.
  useEffect(() => {
    setLines(buildInitialLines(value, suggestedCategories, type, total));
  }, [value, suggestedCategories, type, total]);

  const allocated = (lines[0].amount ?? 0) + (lines[1].amount ?? 0);
  const remaining = total - allocated;
  const isOverAllocated = remaining < 0;
  const pctOfTotal = (amount: number) => (total > 0 ? Math.round((amount / total) * 100) : 0);
  const allocatedPct = Math.min(100, pctOfTotal(allocated));

  const categoriesValid =
    Boolean(lines[0].category) &&
    Boolean(lines[1].category) &&
    lines[0].category !== lines[1].category;
  const amountsValid = (lines[0].amount ?? 0) > 0 && (lines[1].amount ?? 0) > 0 && remaining === 0;
  const isValid = categoriesValid && amountsValid;

  const appliedLines = useMemo(
    () =>
      lines[0].category && lines[1].category
        ? ([
            { category: lines[0].category as TransactionCategory, amount: lines[0].amount ?? 0 },
            { category: lines[1].category as TransactionCategory, amount: lines[1].amount ?? 0 },
          ] as TransactionImportRowLineDTO[])
        : null,
    [lines]
  );

  const isDirty = useMemo(() => {
    if (!value || value.length < 2) return true;
    if (!appliedLines) return true;
    return (
      appliedLines[0].category !== value[0].category ||
      appliedLines[0].amount !== value[0].amount ||
      appliedLines[1].category !== value[1].category ||
      appliedLines[1].amount !== value[1].amount
    );
  }, [appliedLines, value]);

  // With exactly two lines there is a single degree of freedom, so setting one
  // amount auto-fills the other with the remainder and the split always balances.
  const handleAmountChange = (index: 0 | 1, amount: number | null) => {
    setLines((current) => {
      const next: [DraftLine, DraftLine] = [{ ...current[0] }, { ...current[1] }];
      next[index].amount = amount;
      if (amount !== null) {
        const other = index === 0 ? 1 : 0;
        next[other].amount = Math.max(total - amount, 0);
      }
      return next;
    });
  };

  const handleCategoryChange = (index: 0 | 1, category: TransactionCategory | '') => {
    setLines((current) => {
      const next: [DraftLine, DraftLine] = [{ ...current[0] }, { ...current[1] }];
      next[index].category = category;
      return next;
    });
  };

  const splitEvenly = () => {
    const half = Math.floor(total / 2);
    setLines((current) => [
      { ...current[0], amount: half },
      { ...current[1], amount: total - half },
    ]);
  };

  const applyPreset = (categories: SplitPreset) => {
    setLines((current) => [
      { category: categories[0], amount: current[0].amount },
      { category: categories[1], amount: current[1].amount },
    ]);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isOverAllocated ? 'error.main' : isValid ? 'success.main' : 'divider',
        borderRadius: 1,
        p: 2,
        bgcolor: 'background.paper',
        transition: 'border-color 150ms ease',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
        <Typography variant="subtitle2">Desglose en dos categorías</Typography>
        {isValid ? (
          <Chip size="small" color="success" icon={<CheckCircleRoundedIcon />} label="Cuadrado" />
        ) : isOverAllocated ? (
          <Chip
            size="small"
            color="error"
            label={`Te has pasado ${formatCurrency(Math.abs(remaining))}`}
          />
        ) : (
          <Chip size="small" color="warning" label={`Restante ${formatCurrency(remaining)}`} />
        )}
      </Box>

      <LinearProgress
        variant="determinate"
        value={allocatedPct}
        color={isOverAllocated ? 'error' : isValid ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 0.75 }}
      />
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Asignado {formatCurrency(allocated)} de {formatCurrency(total)}
      </Typography>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" alignItems="center" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Atajos:
        </Typography>
        {type === 'EXPENSE' &&
          EXPENSE_PRESETS.map((preset) => (
            <Tooltip key={preset.label} title={preset.tooltip} arrow>
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={() => applyPreset(preset.categories)}
              >
                {preset.label}
              </Button>
            </Tooltip>
          ))}
        <Button type="button" size="small" variant="text" onClick={splitEvenly}>
          Dividir 50/50
        </Button>
      </Stack>

      {[0, 1].map((index) => {
        const line = lines[index as 0 | 1];
        const pct = line.amount && line.amount > 0 ? pctOfTotal(line.amount) : null;
        return (
          <Box key={index}>
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 170px' }} gap={1.5}>
              <TransactionCategorySelect
                value={line.category}
                onChange={(category) => handleCategoryChange(index as 0 | 1, category)}
                type={type}
                size="small"
                label={`Categoría ${index + 1}`}
              />
              <EurCurrencyField
                value={line.amount}
                onChange={(next) => handleAmountChange(index as 0 | 1, next)}
                label={`Importe ${index + 1}`}
                size="small"
                helperText={pct !== null ? `${pct}% del total` : undefined}
              />
            </Box>
          </Box>
        );
      })}

      <Stack direction="row" spacing={1} mt={2.5} justifyContent="flex-end">
        {value && value.length > 0 && (
          <Button type="button" size="small" color="inherit" onClick={onClear}>
            Quitar desglose
          </Button>
        )}
        <Button
          type="button"
          size="small"
          variant="contained"
          disabled={!isValid || !isDirty || !appliedLines}
          onClick={() => appliedLines && onApply(appliedLines)}
        >
          {value && value.length > 0 && !isDirty ? 'Desglose aplicado' : 'Aplicar desglose'}
        </Button>
      </Stack>
    </Box>
  );
};
